<script>
  import Header from "../components/Header.svelte";
  import Footer from "../components/Footer.svelte";
  import { onMount } from "svelte";
  import LandingHeader from "../components/LandingHeader.svelte";

  // Scroll to the top when the component is mounted
  onMount(() => {
    window.scrollTo(0, 0);
  });
</script>

<LandingHeader />
<br /><br />

<body>
  <section class="terms-conditions">
    <h1>Terms & Conditions</h1>
    <p class="last-updated">Last Updated: November 1, 2024</p>

    <p>
      These Terms and Conditions (“Terms”) govern your access to and use of
      TruLens, a digital media verification solution provided by Jionex IT
      Services Co. L.L.C (“Jionex,” “we,” “us,” or “our”), a company
      headquartered in the United Arab Emirates. TruLens is designed to verify
      the source, authenticity, and context of digital media to combat
      misrepresentation, tampering, and deep fakes. By accessing and using
      TruLens, you agree to comply with these Terms. Please review them
      carefully.
    </p>

    <h2>Acceptance of Terms</h2>
    <p>
      By accessing or using TruLens, you agree to be bound by these Terms and
      any additional guidelines, policies, or documents incorporated by
      reference. If you do not accept these Terms, please refrain from using
      TruLens.
    </p>

    <h2>Changes to Terms</h2>
    <p>
      We reserve the right to update or modify these Terms at any time. Any
      updates will be effective upon posting on our website. Continued use of
      TruLens following such changes constitutes your acceptance of the revised
      Terms.
    </p>

    <h2>Definitions</h2>
    <ul>
      <li>
        <strong>User:</strong> An individual or entity accessing and using TruLens
        services.
      </li>
      <li>
        <strong>Verification Data:</strong> Media files (e.g., images, videos, audio)
        and associated metadata provided for authentication and verification purposes.
      </li>
      <li>
        <strong>Services:</strong> All features, functions, applications, and tools
        available through TruLens.
      </li>
    </ul>

    <h2>Use of TruLens Services</h2>
    <ul>
      <li>
        <strong>Permitted Use:</strong> TruLens may only be used for lawful purposes
        to verify the authenticity, source, and context of digital media. Unauthorized
        uses, such as attempting to alter or misuse data verification services, are
        strictly prohibited.
      </li>
      <li>
        <strong>User Responsibilities:</strong> Users are responsible for ensuring
        the accuracy of the media submitted for verification. Users are also responsible
        for maintaining the confidentiality of their account credentials and must
        notify TruLens immediately of any unauthorized access or suspected security
        breach.
      </li>
      <li>
        <strong>Compliance:</strong> By using TruLens, you agree to comply with all
        applicable local and international laws, including data protection and privacy
        laws relevant to digital media verification.
      </li>
    </ul>

    <h2>Account Registration</h2>
    <p>
      To access certain TruLens features, Users may be required to create an
      account and provide accurate, complete information. We reserve the right
      to terminate accounts or restrict access if inaccurate or incomplete
      information is provided, or if there is any misuse of the platform.
    </p>

    <h2>Intellectual Property</h2>
    <ul>
      <li>
        <strong>Ownership:</strong> TruLens, including all associated content, features,
        and functionalities, is owned by Jionex and protected by copyright, trademark,
        and other intellectual property laws.
      </li>
      <li>
        <strong>License:</strong> TruLens grants Users a limited, non-exclusive,
        non-transferable license to access and use TruLens for lawful verification
        purposes. Users agree not to copy, modify, distribute, or create derivative
        works from TruLens content.
      </li>
      <li>
        <strong>Restrictions:</strong> Users shall not reverse engineer, decompile,
        or otherwise attempt to extract the source code of TruLens. Unauthorized
        use of TruLens intellectual property is strictly prohibited.
      </li>
    </ul>

    <h2>Fees and Payment</h2>
    <p>
      TruLens offers various subscription and usage-based services that may
      incur fees. All applicable fees are disclosed at the time of registration
      or service request. We reserve the right to modify pricing at any time,
      with notice provided to Users. Users are responsible for all applicable
      fees and any additional taxes.
    </p>

    <h2>Data Collection and Privacy</h2>
    <ul>
      <li>
        <strong>Data Handling:</strong> Any information collected through TruLens,
        including Verification Data, is handled in accordance with our [Privacy Policy].
        Users retain ownership of media files submitted for verification.
      </li>
      <li>
        <strong>Security Measures:</strong> TruLens employs advanced security measures,
        including encryption, to protect Verification Data. However, we cannot guarantee
        absolute security, and Users acknowledge the inherent risks associated with
        digital transactions.
      </li>
    </ul>

    <h2>Limitations of Liability</h2>
    <ul>
      <li>
        <strong>No Warranties:</strong> TruLens is provided “as is” without warranties
        of any kind, express or implied. We do not warrant that TruLens will meet
        your specific requirements, operate without interruption, or be error-free.
      </li>
      <li>
        <strong>Limitation of Liability:</strong> To the extent permitted by law,
        Jionex shall not be liable for any indirect, incidental, consequential, or
        punitive damages arising from the use or inability to use TruLens. Our total
        liability, if any, shall not exceed the amount paid by you for TruLens services
        in the six months preceding the claim.
      </li>
      <li>
        <strong>Indemnification:</strong> You agree to indemnify and hold Jionex
        harmless from any claims, damages, liabilities, or expenses arising from
        your use of TruLens, your breach of these Terms, or any infringement on the
        rights of a third party.
      </li>
    </ul>

    <h2>Service Suspension and Termination</h2>
    <ul>
      <li>
        <strong>Suspension:</strong> We reserve the right to suspend access to TruLens
        if a User engages in any unauthorized use, violates these Terms, or poses
        a security risk.
      </li>
      <li>
        <strong>Termination by User:</strong> Users may terminate their accounts
        at any time by providing notice to TruLens. Termination does not relieve
        the User from any outstanding payment obligations.
      </li>
      <li>
        <strong>Effects of Termination:</strong> Upon termination, TruLens will retain
        Verification Data only as required by law or regulatory obligations. All
        user access to TruLens services will cease immediately.
      </li>
    </ul>

    <h2>Governing Law and Dispute Resolution</h2>
    <ul>
      <li>
        <strong>Governing Law:</strong> These Terms are governed by the laws of the
        United Arab Emirates, without regard to its conflict of laws principles.
      </li>
      <li>
        <strong>Dispute Resolution:</strong> Any dispute arising from these Terms
        shall be resolved through negotiation, and if unresolved, through arbitration
        in the UAE, in accordance with UAE arbitration laws.
      </li>
    </ul>

    <h2>Confidentiality</h2>
    <p>
      TruLens will treat all media and Verification Data as confidential and
      will not disclose it to third parties without consent, except as required
      by law. Users agree to maintain the confidentiality of any non-public
      information accessed through TruLens.
    </p>

    <h2>Amendments</h2>
    <p>
      TruLens reserves the right to modify these Terms at any time. Any changes
      will be posted on our website, and Users are encouraged to review these
      Terms regularly. Continued use of TruLens after such modifications
      indicates acceptance of the updated Terms.
    </p>

    <h2>Contact Information</h2>
    <p>
      If you have questions or concerns regarding this Privacy Policy or your
      personal information, please contact us at:
    </p>
    <p>
      <strong>Jionex IT Services Co. L.L.C</strong><br />
      Head Office: M5, Liberty Building, 18th Service Road, Al Garhoud, Dubai, U.A.E<br
      />
      Email: admin@trulens.ae || contact@jionex.com
    </p>
  </section>
</body>

<br /><br />

<Footer />

<style>
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
  }

  .terms-conditions {
    max-width: 95%;
    margin: auto;
    padding-top: 0 !important;
    padding: 20px;
    line-height: 1.6;
  }

  h1 {
    font-size: 3em;
    color: #003366;
    margin-bottom: 10px;
    text-align: start;
  }

  .last-updated {
    font-size: 1em;
    color: #666;
    text-align: start;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 1.5em;
    color: #003366;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  h3 {
    font-size: 1.25em;
    color: #004080;
    margin-top: 15px;
    margin-bottom: 5px;
  }

  ul {
    padding-left: 20px;
    margin-top: 10px;
  }

  ul li {
    margin-bottom: 8px;
  }

  p {
    font-size: 1em;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  /* Media Queries */
  @media (max-width: 768px) {
    h1 {
      font-size: 1.8em;
    }

    h2 {
      font-size: 1.4em;
    }

    h3 {
      font-size: 1.2em;
    }
  }

  @media (max-width: 480px) {
    h1 {
      font-size: 1.6em;
    }

    h2 {
      font-size: 1.2em;
    }

    h3 {
      font-size: 1em;
    }

    .terms-conditions {
      padding: 10px;
    }
  }

  @media (max-width: 390px) {
    h1 {
      font-size: 1.5em;
    }

    h2 {
      font-size: 1.1em;
    }

    h3 {
      font-size: 0.9em;
    }

    .terms-conditions {
      padding: 8px;
    }
  }
</style>
